import React, { useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";

import PageTitle from "../../../../layouts/PageTitle";
import { appApi, useGetContractorCategoriesQuery } from "../../../../../store/appApi";
import {
  addContractorCategoryApi,
  updateContractorCategoryApi,
  deleteContractorCategoryApi,
} from "../../../../../services/contractor";

const EMPTY_FORM = { _id: "", name: "" };

const ContractorCategory = () => {
  const dispatch = useDispatch();

  // ── GET via Redux ────────────────────────────────────────────────────────
  const {
    data: categoriesResponse,
    isLoading: loadingList,
  } = useGetContractorCategoriesQuery();

  const categories = Array.isArray(categoriesResponse?.data)
    ? categoriesResponse.data
    : [];

  // ── Add form state ────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [addError, setAddError] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deletingId, setDeletingId] = useState(null);

  // ── POST: Add category — direct Axios, invalidate cache ──────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setAddError("Category name is required."); return; }
    setSaving(true);
    setAddError("");
    try {
      await addContractorCategoryApi({ name: trimmed });
      dispatch(appApi.util.invalidateTags(["ContractorCategories"]));
      setName("");
      Swal.fire({ icon: "success", title: "Category added!", timer: 1200, showConfirmButton: false });
    } catch (err) {
      setAddError(err?.response?.data?.message || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  // ── Open edit modal ────────────────────────────────────────────────────────
  const openEdit = (cat) => {
    setEditForm({ _id: cat.id || cat._id, name: cat.name });
    setEditError("");
    setEditModal(true);
  };

  // ── PATCH: Update category — direct Axios, invalidate cache ───────────────
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { setEditError("Category name is required."); return; }
    setEditSaving(true);
    setEditError("");
    try {
      await updateContractorCategoryApi(editForm._id, { name: editForm.name.trim() });
      dispatch(appApi.util.invalidateTags(["ContractorCategories"]));
      setEditModal(false);
      Swal.fire({ icon: "success", title: "Category updated!", timer: 1200, showConfirmButton: false });
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to update category.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── DELETE: direct Axios, invalidate cache ────────────────────────────────
  const handleDelete = async (cat) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete category?",
      text: `"${cat.name}" will be permanently removed.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    setDeletingId(cat.id || cat._id);
    try {
      await deleteContractorCategoryApi(cat.id || cat._id);
      dispatch(appApi.util.invalidateTags(["ContractorCategories"]));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to delete category.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Contractor Categories" motherMenu="Contractors" />

      {/* ── Edit modal ── */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Category</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleUpdate}>
          <Modal.Body>
            <div className="form-group mb-0">
              <label className="form-label">Category Name</label>
              <input
                type="text"
                className={`form-control ${editError ? "is-invalid" : ""}`}
                value={editForm.name}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, name: e.target.value }));
                  setEditError("");
                }}
                autoFocus
              />
              {editError && <div className="invalid-feedback">{editError}</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editSaving}>
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <div className="row">
        {/* ── Add form ── */}
        <div className="col-xl-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Add Contractor Category</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleAdd}>
                <div className="mb-3">
                  <label className="form-label">Category Name</label>
                  <input
                    type="text"
                    className={`form-control ${addError ? "is-invalid" : ""}`}
                    placeholder="e.g. Civil, Electrical, etc."
                    value={name}
                    onChange={(e) => { setName(e.target.value); setAddError(""); }}
                  />
                  {addError && <div className="invalid-feedback">{addError}</div>}
                </div>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Add Category"}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── Category list ── */}
        <div className="col-xl-8">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">All Categories</h5>
            </div>
            <div className="card-body p-0">
              {loadingList ? (
                <div className="p-4 text-center text-muted">Loading categories...</div>
              ) : categories.length === 0 ? (
                <div className="p-4 text-center text-muted">No categories found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered verticle-middle mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat, i) => (
                        <tr key={cat.id || cat._id}>
                          <td>{i + 1}</td>
                          <td style={{ textTransform: "capitalize" }}>{cat.name}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-primary"
                                onClick={() => openEdit(cat)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-danger"
                                disabled={deletingId === (cat.id || cat._id)}
                                onClick={() => handleDelete(cat)}
                              >
                                {deletingId === (cat.id || cat._id) ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractorCategory;
